import React from 'react';

/**
 * Emits per-market SEO/GEO head tags when a market is actively viewed,
 * using React 19's native metadata hoisting (no external library required).
 *
 * Delivers:
 *  - A market-specific <title>/<description> so deep links shared to X, Slack,
 *    Discord, etc. surface the actual market rather than the generic homepage.
 *  - JSON-LD "Event", "FinancialProduct", and "BreadcrumbList" blocks so
 *    Google rich results and LLM answer engines (Perplexity, ChatGPT Search,
 *    Gemini, Claude) can quote the market accurately with its closing date,
 *    category, and current probability.
 *
 * Safe to render when `market` is null/undefined — renders nothing.
 */
const MarketJsonLd = ({ market }) => {
  if (!market || !market.id || !market.title) return null;

  const baseUrl = 'https://amppredict.com';
  const canonical = `${baseUrl}/?market=${encodeURIComponent(market.id)}`;
  const title = `${market.title} | AMP Predict`;
  const desc =
    (market.description || '').trim().slice(0, 240) ||
    `Trade YES/NO contracts on "${market.title}" on AMP Predict — a mock prediction market for educational purposes.`;

  const yesPrice =
    typeof market.yes_price === 'number'
      ? market.yes_price
      : typeof market.yesPrice === 'number'
      ? market.yesPrice
      : null;

  const event = {
    '@context': 'https://schema.org',
    '@type': 'Event',
    name: market.title,
    description: desc,
    url: canonical,
    eventStatus: 'https://schema.org/EventScheduled',
    eventAttendanceMode: 'https://schema.org/OnlineEventAttendanceMode',
    startDate: market.opens_at || market.created_at || undefined,
    endDate: market.closes_at || undefined,
    location: {
      '@type': 'VirtualLocation',
      url: canonical,
    },
    organizer: {
      '@type': 'Organization',
      name: 'AMP Predict',
      url: baseUrl,
    },
    about: market.category || 'Prediction markets',
  };

  const product = {
    '@context': 'https://schema.org',
    '@type': 'FinancialProduct',
    name: `YES/NO contract — ${market.title}`,
    description: desc,
    url: canonical,
    provider: {
      '@type': 'Organization',
      name: 'AMP Predict',
      url: baseUrl,
    },
    category: market.category || 'Prediction market',
    ...(yesPrice != null && {
      offers: {
        '@type': 'Offer',
        priceCurrency: 'USD',
        price: (yesPrice / 100).toFixed(2),
        availability:
          market.status === 'OPEN'
            ? 'https://schema.org/InStock'
            : 'https://schema.org/Discontinued',
        url: canonical,
      },
    }),
  };

  const breadcrumb = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: baseUrl },
      {
        '@type': 'ListItem',
        position: 2,
        name: market.category || 'Markets',
        item: market.category
          ? `${baseUrl}/?category=${encodeURIComponent(market.category)}`
          : baseUrl,
      },
      { '@type': 'ListItem', position: 3, name: market.title, item: canonical },
    ],
  };

  return (
    <>
      <title>{title}</title>
      <meta name="description" content={desc} />
      <link rel="canonical" href={canonical} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={desc} />
      <meta property="og:url" content={canonical} />
      <meta property="og:type" content="article" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={desc} />
      <script
        type="application/ld+json"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: JSON.stringify(event) }}
      />
      <script
        type="application/ld+json"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: JSON.stringify(product) }}
      />
      <script
        type="application/ld+json"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumb) }}
      />
    </>
  );
};

export default MarketJsonLd;
